import { createApi, fetchBaseQuery, BaseQueryFn } from '@reduxjs/toolkit/query/react';
import { getApiConfig } from '@/config/api';
import { getRequestLanguage } from '@/utils/requestLanguage';
import type { ApiResponse } from '@/types';

// Store Types
export interface Store {
  id: string;
  name: string;
  ownerId: string;
  address?: string | null;
  phoneNumber?: string | null;
  email?: string | null;
  features: StoreFeatures;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  owner?: {
    id: string;
    name: string;
    username: string;
  };
  outlets?: Outlet[];
  _count?: {
    outlets: number;
    userStores: number;
  };
}

export interface StoreFeatures {
  cashier?: boolean;
  inventory?: boolean;
  expense?: boolean;
  reports?: boolean;
  customers?: boolean;
  multiplePayments?: boolean;
  loyaltyProgram?: boolean;
  stockAlerts?: boolean;
  batchOperations?: boolean;
  api?: boolean;
}

export interface Outlet {
  id: string;
  storeId: string;
  name: string;
  code?: string | null;
  address?: string | null;
  phoneNumber?: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateStoreDto {
  name: string;
  address?: string;
  phoneNumber?: string;
  email?: string;
  features?: StoreFeatures;
}

export interface UpdateStoreDto {
  name?: string;
  address?: string;
  phoneNumber?: string;
  email?: string;
  isActive?: boolean;
}

export interface StoreFeaturesDto {
  cashier?: boolean;
  inventory?: boolean;
  expense?: boolean;
  reports?: boolean;
  customers?: boolean;
  multiplePayments?: boolean;
  loyaltyProgram?: boolean;
  stockAlerts?: boolean;
  batchOperations?: boolean;
  api?: boolean;
}

export interface TransferOwnershipDto {
  newOwnerId: string;
  transferReason?: string;
}

export interface StoresResponse {
  stores: Store[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

// Create a dynamic base query for store API
const dynamicStoreBaseQuery: BaseQueryFn = async (args, api, extraOptions) => {
  const config = getApiConfig();

  const baseQuery = fetchBaseQuery({
    baseUrl: config.API_URL,
    prepareHeaders: (headers, { getState }) => {
      let token = (getState() as any).auth.token;
      if (!token) {
        token = localStorage.getItem('token');
      }
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      headers.set('ngrok-skip-browser-warning', 'true');
      headers.set('accept-language', getRequestLanguage());
      return headers;
    },
  });

  return baseQuery(args, api, extraOptions);
};

export const storeApi = createApi({
  reducerPath: 'storeApi',
  baseQuery: dynamicStoreBaseQuery,
  tagTypes: ['Store'],
  endpoints: (builder) => ({
    // Get all stores
    getStores: builder.query<ApiResponse<Store[]>, {
      page?: number;
      limit?: number;
      search?: string;
      isActive?: boolean;
    }>({
      query: (params) => ({
        url: '/stores',
        params,
      }),
      providesTags: ['Store'],
    }),

    // Get single store
    getStore: builder.query<ApiResponse<Store>, string>({
      query: (id) => `/stores/${id}`,
      providesTags: (_, __, id) => [{ type: 'Store', id }],
    }),

    // Create store
    createStore: builder.mutation<ApiResponse<Store>, CreateStoreDto>({
      query: (data) => ({
        url: '/stores',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Store'],
    }),
    updateStore: builder.mutation<
      ApiResponse<Store>,
      { id: string; data: UpdateStoreDto }
    >({
      query: ({ id, data }) => ({
        url: `/stores/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_, __, { id }) => [{ type: 'Store', id }, 'Store'],
    }),

    // Delete store
    deleteStore: builder.mutation<ApiResponse<{ message: string }>, { id: string; force?: boolean }>({
      query: ({ id, force }) => ({
        url: `/stores/${id}`,
        method: 'DELETE',
        params: force ? { force: 'true' } : undefined,
      }),
      invalidatesTags: ['Store'],
    }),

    // Get store features
    getStoreFeatures: builder.query<ApiResponse<{
      storeId: string;
      storeName: string;
      features: StoreFeatures;
    }>, string>({
      query: (id) => `/stores/${id}/features`,
      providesTags: (_, __, id) => [{ type: 'Store', id }],
    }),

    // Update store features
    updateStoreFeatures: builder.mutation<
      ApiResponse<{
        id: string;
        name: string;
        features: StoreFeatures;
      }>,
      { id: string; features: StoreFeaturesDto }
    >({
      query: ({ id, features }) => ({
        url: `/stores/${id}/features`,
        method: 'PATCH',
        body: features,
      }),
      invalidatesTags: (_, __, { id }) => [{ type: 'Store', id }, 'Store'],
    }),

    // Transfer ownership
    transferOwnership: builder.mutation<
      ApiResponse<{
        message: string;
        store: Store;
      }>,
      { id: string; data: TransferOwnershipDto }
    >({
      query: ({ id, data }) => ({
        url: `/stores/${id}/transfer-ownership`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_, __, { id }) => [{ type: 'Store', id }, 'Store'],
    }),
  }),
});

export const {
  useGetStoresQuery,
  useGetStoreQuery,
  useCreateStoreMutation,
  useUpdateStoreMutation,
  useDeleteStoreMutation,
  useGetStoreFeaturesQuery,
  useUpdateStoreFeaturesMutation,
  useTransferOwnershipMutation,
} = storeApi;
